import { Button } from '@/components/ui/button';
import { FlexCol } from '@/components/ui/flex';
import { Textarea } from '@/components/ui/textarea';
import { ProductListDetail } from '@/pages/Settings/components/ProductListDetail';
import { useEditableMarkdown } from '@/shared/EditableMarkdown';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import {
  useOnboardingCompleteProfileService,
  useOnboardingGenerateProfileService,
  useOnboardingRecommendCategoriesService,
  useProfileService,
  useSourceSubscriptionsService,
} from '@/services/regulatoryWatch';
import {
  captureRegulatoryOnboardingEvent,
  LoadingStepRow,
  OnboardingCard,
  OnboardingLayout,
  OnboardingResultsTabbed,
  OnboardingStepper,
  PROFILE_LOADING_STEPS,
  ProfileForm,
  toggleInArray,
  useLoadingSteps,
  useRegulatoryOnboardingDocumentTitle,
} from '@/shared/onboarding';
import {
  ChevronLeft,
  ChevronRight,
  Link2,
  Loader2,
  Pencil,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { useParams } from 'react-router-dom';

type SignedInStep = 'url-input' | 'loading' | 'review' | 'products' | 'results';

const STEP_STORAGE_PREFIX = 'onboarding_step:';

function getStepKey(workspaceUname: string) {
  return `${STEP_STORAGE_PREFIX}${workspaceUname}`;
}

function getRestoredStep(workspaceUname: string): SignedInStep {
  try {
    const stored = localStorage.getItem(
      getStepKey(workspaceUname)
    ) as SignedInStep | null;
    if (stored === 'loading') return 'url-input';
    if (stored === 'review' || stored === 'products' || stored === 'results')
      return stored;
  } catch {}
  return 'url-input';
}

export const OnboardingWizard: FC = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const [step, setStepRaw] = useState<SignedInStep>(() =>
    getRestoredStep(workspaceUname!)
  );

  const setStep = useCallback(
    (next: SignedInStep) => {
      setStepRaw(next);
      try {
        localStorage.setItem(getStepKey(workspaceUname!), next);
      } catch {}
    },
    [workspaceUname]
  );
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [profileApiDone, setProfileApiDone] = useState(false);

  useRegulatoryOnboardingDocumentTitle(step);

  // Services — all DB-backed
  const { profile, updateProfile } = useProfileService();
  const { onboardingGenerateProfile } = useOnboardingGenerateProfileService();
  const { onboardingCompleteProfile, isCompleting } =
    useOnboardingCompleteProfileService();
  const {
    fireAll: fireRecommendations,
    isRegulationsLoading,
    isGuidancesLoading,
    isStandardsLoading,
    isNewsLoading,
    isAnyLoading: isRecommending,
  } = useOnboardingRecommendCategoriesService();
  const { sourcesWithSubscriptions, bulkUpdateSubscriptions } =
    useSourceSubscriptionsService();

  // Profile form state — initialized from DB, edits debounced to DB
  const [companyDescription, setCompanyDescription] = useState<string>('');
  const [markets, setMarkets] = useState<string[]>([]);
  const [riskClassifications, setRiskClassifications] = useState<string[]>([]);
  const [deviceCharacteristics, setDeviceCharacteristics] = useState<string[]>(
    []
  );
  const [therapeuticAreas, setTherapeuticAreas] = useState<string[]>([]);
  const hasUserEditedProfile = useRef(false);
  const descriptionDebounceRef =
    useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync profile form state from DB
  useEffect(() => {
    if (profile && !hasUserEditedProfile.current) {
      setCompanyDescription(profile.companyDescription ?? '');
      setMarkets(profile.markets ?? []);
      setRiskClassifications(profile.riskClassifications ?? []);
      setDeviceCharacteristics(profile.deviceCharacteristics ?? []);
      setTherapeuticAreas(profile.therapeuticAreas ?? []);
    }
  }, [profile]);

  // Debounced auto-save profile edits to DB
  useEffect(() => {
    if (!hasUserEditedProfile.current) return;

    const timer = setTimeout(() => {
      hasUserEditedProfile.current = false;
      updateProfile({
        markets,
        riskClassifications,
        deviceCharacteristics,
        therapeuticAreas,
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [markets, riskClassifications, deviceCharacteristics, therapeuticAreas]);

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setCompanyDescription(value);
      clearTimeout(descriptionDebounceRef.current);
      descriptionDebounceRef.current = setTimeout(() => {
        updateProfile({ companyDescription: value });
      }, 800);
    },
    [updateProfile]
  );

  useEffect(() => {
    return () => clearTimeout(descriptionDebounceRef.current);
  }, []);

  const {
    isEditing: isEditingDescription,
    textareaProps: descriptionTextareaProps,
    onPreviewClick: onDescriptionPreviewClick,
    onPreviewKeyDown: onDescriptionPreviewKeyDown,
  } = useEditableMarkdown({
    value: companyDescription,
    onChange: handleDescriptionChange,
  });

  // Loading animation
  const profileLoadingStatuses = useLoadingSteps(
    step === 'loading',
    profileApiDone,
    PROFILE_LOADING_STEPS
  );
  const allProfileStepsDone = profileLoadingStatuses.every(s => s === 'done');

  useEffect(() => {
    if (allProfileStepsDone && profileApiDone) {
      const timeout = setTimeout(() => setStep('review'), 600);
      return () => clearTimeout(timeout);
    }
  }, [allProfileStepsDone, profileApiDone]);

  // Build subscription data for OnboardingResultsTabbed from DB
  const subscribedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const s of sourcesWithSubscriptions) {
      if (s.subscriptionStatus === 'subscribed') ids.add(s.id);
    }
    return ids;
  }, [sourcesWithSubscriptions]);

  // AI Act and IVDR source IDs — always show justification even if not subscribed
  const AI_CHECK_SOURCE_IDS = new Set([
    'eu_ivdr_law',
    'eu_ivdr_newsfeed',
    'eu_ai_act_law',
    'eu_ai_act_newsfeed',
  ]);

  const toSourceItem = useCallback(
    (s: (typeof sourcesWithSubscriptions)[0]) => ({
      id: s.id,
      title: s.title,
      description: s.description,
      referenceTypes: s.referenceTypes,
      geography: s.geography,
      institutionGeographies: s.institutionGeographies,
      institutionName: s.institutionName,
      icon: s.icon,
      // Show justification for subscribed sources + AI Act/IVDR always
      reasoning:
        s.subscriptionStatus === 'subscribed' || AI_CHECK_SOURCE_IDS.has(s.id)
          ? s.aiJustification
          : null,
    }),
    []
  );

  const makeCategoryData = useCallback(
    (filter: (s: (typeof sourcesWithSubscriptions)[0]) => boolean) => {
      const filtered = sourcesWithSubscriptions.filter(filter);
      return {
        sources: filtered.map(toSourceItem),
        totalCount: filtered.length,
        recommendedCount: filtered.filter(
          s => s.subscriptionStatus === 'subscribed'
        ).length,
      };
    },
    [sourcesWithSubscriptions, toSourceItem]
  );

  const regulationsData = useMemo(
    () =>
      !isRegulationsLoading
        ? makeCategoryData(s => s.referenceTypes.some(t => t === 'regulation'))
        : null,
    [isRegulationsLoading, makeCategoryData]
  );

  const guidancesData = useMemo(
    () =>
      !isGuidancesLoading
        ? makeCategoryData(s => s.referenceTypes.some(t => t === 'guidance'))
        : null,
    [isGuidancesLoading, makeCategoryData]
  );

  const standardsData = useMemo(
    () =>
      !isStandardsLoading
        ? makeCategoryData(s => s.referenceTypes.includes('standard'))
        : null,
    [isStandardsLoading, makeCategoryData]
  );

  const newsData = useMemo(
    () =>
      !isNewsLoading
        ? makeCategoryData(s => s.referenceTypes.includes('news'))
        : null,
    [isNewsLoading, makeCategoryData]
  );

  const handleToggleSource = useCallback(
    (sourceId: string) => {
      const source = sourcesWithSubscriptions.find(s => s.id === sourceId);
      const isSubscribed = source?.subscriptionStatus === 'subscribed';
      bulkUpdateSubscriptions({
        subscriptions: [
          { sourceId, status: isSubscribed ? 'unsubscribed' : 'subscribed' },
        ],
      });
    },
    [sourcesWithSubscriptions, bulkUpdateSubscriptions]
  );

  // Handlers
  const handleAnalyze = () => {
    if (!url.trim()) return;
    setError(null);
    setProfileApiDone(false);
    setStep('loading');
    onboardingGenerateProfile(url.trim(), {
      onSuccess: () => {
        setProfileApiDone(true);
      },
      onError: (err: Error) => {
        setProfileApiDone(true);
        setError(
          err.message ||
            'Failed to analyze the website. You can fill in your profile manually.'
        );
      },
    });
  };

  const handleToggleMarket = (market: string) => {
    hasUserEditedProfile.current = true;
    const updated = toggleInArray(markets, market);
    setMarkets(updated);

    if (!updated.includes(market)) {
      const prefix = market === 'US' ? 'US ' : 'EU ';
      setRiskClassifications(prev => prev.filter(rc => !rc.startsWith(prefix)));
    }
  };

  const handleProfileFieldChange = (
    setter: (value: string[]) => void,
    value: string[]
  ) => {
    hasUserEditedProfile.current = true;
    setter(value);
  };

  const handleContinueToResults = () => {
    captureRegulatoryOnboardingEvent('regulatory_onboarding_step_completed', {
      step: 'products',
      flow: 'signed_in',
    });

    setStep('results');
    fireRecommendations({
      onError: () => {
        setError('Failed to generate recommendations. Please try again.');
      },
    });
  };

  const handleComplete = () => {
    onboardingCompleteProfile({
      onError: () => {
        setError('Failed to complete onboarding. Please try again.');
      },
    });
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (step === 'url-input') {
    return (
      <OnboardingLayout
        currentStep={step}
        variant="light"
        stepper={
          <OnboardingStepper
            currentStep={step}
            variant="light"
            showEmailStep={false}
          />
        }
      >
        <OnboardingCard
          header={
            <>
              <h2 className="text-lg font-semibold text-neutral-900 tracking-tight">
                Enter company website
              </h2>
              <p className="text-sm text-neutral-600">
                Add your company website and we will automatically generate your
                company profile, saving you up to 15 minutes of manual setup.
              </p>
              <p className="text-sm text-neutral-400">
                We do not store your website data.
              </p>
            </>
          }
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                className="h-9 px-4 text-sm font-medium text-neutral-900"
                onClick={() => setStep('review')}
              >
                Skip
              </Button>
              <Button
                className="h-9 px-4 rounded-lg bg-[#130e30] text-white text-sm font-medium hover:bg-[#130e30]/90"
                onClick={handleAnalyze}
                disabled={!url.trim()}
              >
                Continue
              </Button>
            </div>
          }
        >
          <div className="p-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-900">
                Company website
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Link2 className="size-5 text-neutral-500" />
                </div>
                <input
                  type="text"
                  className="w-full h-9 pl-10 pr-3 rounded-lg border border-neutral-300 bg-white text-sm placeholder:text-neutral-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#130e30]/20 focus:border-[#130e30]"
                  placeholder="https://yourcompany.com"
                  value={url}
                  onChange={e => {
                    setUrl(e.target.value);
                    setError(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleAnalyze();
                  }}
                />
              </div>
              {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
            </div>
          </div>
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  if (step === 'loading') {
    return (
      <OnboardingLayout
        currentStep={step}
        variant="light"
        stepper={
          <OnboardingStepper
            currentStep={step}
            variant="light"
            showEmailStep={false}
          />
        }
      >
        <OnboardingCard
          header={
            <>
              <h2 className="text-lg font-semibold text-neutral-900 tracking-tight">
                Analyzing your company
              </h2>
              <p className="text-sm text-neutral-600">
                We&apos;re extracting information from your website...
              </p>
            </>
          }
        >
          <div className="p-6">
            <FlexCol gap="4">
              {PROFILE_LOADING_STEPS.map((def, i) => (
                <LoadingStepRow
                  key={def.title}
                  def={def}
                  status={profileLoadingStatuses[i]!}
                />
              ))}
            </FlexCol>
          </div>
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  if (step === 'review') {
    return (
      <OnboardingLayout
        currentStep={step}
        variant="light"
        stepper={
          <OnboardingStepper
            currentStep={step}
            variant="light"
            showEmailStep={false}
          />
        }
      >
        <OnboardingCard
          width="wide"
          header={
            <>
              <h2 className="text-lg font-semibold text-neutral-900 tracking-tight">
                Review company profile
              </h2>
              <p className="text-sm text-neutral-600">
                Please review the categories for relevance. Your profile helps
                us personalize your monitoring subscriptions.
              </p>
            </>
          }
          footer={
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                className="h-9 px-4 text-sm font-medium text-neutral-900 gap-2"
                onClick={() => setStep('url-input')}
              >
                <ChevronLeft className="size-4" />
                Back
              </Button>
              <Button
                className="h-9 px-4 rounded-lg bg-[#130e30] text-white text-sm font-medium hover:bg-[#130e30]/90"
                onClick={() => setStep('products')}
              >
                Continue
              </Button>
            </div>
          }
        >
          <div className="flex-1 overflow-y-auto p-6 pt-8 min-h-0 flex flex-col gap-6">
            {error && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {error}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-neutral-900">
                Company description
              </label>
              {isEditingDescription ? (
                <Textarea
                  {...descriptionTextareaProps}
                  className="min-h-[100px] w-full resize-none border-neutral-300 overflow-hidden"
                  placeholder="Describe your company..."
                />
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={onDescriptionPreviewClick}
                  onKeyDown={onDescriptionPreviewKeyDown}
                  className="group relative min-h-[80px] w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 cursor-pointer transition-colors hover:border-dashed hover:border-neutral-400 hover:bg-neutral-100/50"
                >
                  <Pencil className="absolute top-3 right-3 size-3.5 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100" />
                  {companyDescription ? (
                    <MarkdownText size="sm">{companyDescription}</MarkdownText>
                  ) : (
                    <span className="text-sm text-neutral-500">
                      Click to add a company description...
                    </span>
                  )}
                </div>
              )}
            </div>
            <ProfileForm
              markets={markets}
              onToggleMarket={handleToggleMarket}
              riskClassifications={riskClassifications}
              onSetRiskClassifications={v =>
                handleProfileFieldChange(setRiskClassifications, v)
              }
              deviceCharacteristics={deviceCharacteristics}
              onSetDeviceCharacteristics={v =>
                handleProfileFieldChange(setDeviceCharacteristics, v)
              }
              therapeuticAreas={therapeuticAreas}
              onSetTherapeuticAreas={v =>
                handleProfileFieldChange(setTherapeuticAreas, v)
              }
              hideDescription
            />
          </div>
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  if (step === 'products') {
    return (
      <OnboardingLayout
        currentStep={step}
        variant="light"
        stepper={
          <OnboardingStepper
            currentStep={step}
            variant="light"
            showEmailStep={false}
          />
        }
      >
        <OnboardingCard
          width="wide"
          header={
            <>
              <h2 className="text-lg font-semibold text-neutral-900 tracking-tight">
                Review products
              </h2>
              <p className="text-sm text-neutral-600">
                Confirm or edit the products we detected so we can match the
                right standards and sources to your portfolio.
              </p>
            </>
          }
          footer={
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                className="h-9 px-4 text-sm font-medium text-neutral-900 gap-2"
                onClick={() => setStep('review')}
              >
                <ChevronLeft className="size-4" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  className="h-9 px-4 text-sm font-medium text-neutral-900"
                  onClick={handleContinueToResults}
                >
                  Skip
                </Button>
                <Button
                  className="h-9 px-4 rounded-lg bg-[#130e30] text-white text-sm font-medium hover:bg-[#130e30]/90"
                  onClick={handleContinueToResults}
                >
                  Continue
                </Button>
              </div>
            </div>
          }
        >
          <div className="flex-1 overflow-y-auto p-6 min-h-0 flex flex-col gap-6">
            {profile?.products ? (
              <ProductListDetail products={profile.products} />
            ) : (
              <p className="text-sm text-neutral-500">No products detected.</p>
            )}
          </div>
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  if (step === 'results') {
    return (
      <OnboardingLayout
        currentStep={step}
        variant="light"
        stepper={
          <OnboardingStepper
            currentStep={step}
            variant="light"
            showEmailStep={false}
          />
        }
      >
        <OnboardingCard
          width="wide"
          header={
            <>
              <h2 className="text-lg font-semibold text-neutral-900 tracking-tight">
                Review applicable sources
              </h2>
              <p className="text-sm text-neutral-600">
                {isRecommending
                  ? 'We are matching your profile with our regulatory database...'
                  : 'Based on your device profile, we have identified the most relevant sources. Toggle to customize.'}
              </p>
            </>
          }
          footer={
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                className="h-9 px-4 text-sm font-medium text-neutral-900 gap-2"
                onClick={() => setStep('products')}
              >
                <ChevronLeft className="size-4" />
                Back
              </Button>
              <Button
                className="h-9 px-4 rounded-lg bg-[#130e30] text-white text-sm font-medium hover:bg-[#130e30]/90 gap-1"
                onClick={handleComplete}
                disabled={isCompleting || isRecommending}
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Start tracking updates
                    <ChevronRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          }
        >
          <div className="p-6">
            <OnboardingResultsTabbed
              regulationsData={regulationsData}
              guidancesData={guidancesData}
              standardsData={standardsData}
              newsData={newsData}
              regulationsLoading={isRegulationsLoading}
              guidancesLoading={isGuidancesLoading}
              standardsLoading={isStandardsLoading}
              newsLoading={isNewsLoading}
              selectedSourceIds={subscribedIds}
              onToggleSource={handleToggleSource}
            />
          </div>
          {error && <p className="text-sm text-red-500 px-6 pb-4">{error}</p>}
        </OnboardingCard>
      </OnboardingLayout>
    );
  }

  return null;
};
